/**
 * Match Status Constants
 * Defined in: com.scorer.scorebuzzer.enums.Definitions
 */
export const MatchStatus = {
  MATCH_SCHEDULED: 1,
  MATCH_NOT_STARTED: 2,  // upcoming
  MATCH_STARTED: 3,      // playing
  MATCH_COMPLETED: 4,    // completed
  MATCH_SUSPENDED: 5,    // paused
  MATCH_RETIRED: 6,      // retired
  MATCH_LOCKED: 7,
  MATCH_WALKOVER: 8,
  MATCH_DEFAULT: 9,
  MATCH_INTERRUPTED: 10,
  MATCH_MANUAL: 11,
} as const;

export type MatchStatusType = typeof MatchStatus[keyof typeof MatchStatus];

/**
 * Umpire Mode Constants
 * Defined in: com.scorer.scorebuzzer.enums.Definitions
 */
export const UmpireMode = {
  UMPIRE_MODE_PLAYER: 1,
  UMPIRE_MODE_CU_BASIC: 2,
  UMPIRE_MODE_ROAMING: 3,
  UMPIRE_MODE_CU_ADVANCED: 4,
  UMPIRE_MODE_ADMIN_PLAYER: 5,
  UMPIRE_MODE_POINT_BY_POINT: 6,
  UMPIRE_MODE_GAME_BY_GAME: 7,
  UMPIRE_MODE_PICKLEBALL: 8,
  UMPIRE_MODE_PICKLEBALL_QUICKSCORE: 9,
  UMPIRE_MODE_BEACH_TENNIS: 10,
} as const;

export type UmpireModeType = typeof UmpireMode[keyof typeof UmpireMode];

/**
 * Server Tracking Constants
 * Defined in: ScoreboardData.kt companion object
 */
export const ServerTracking = {
  NO_SERVER: 0,
  PLAYER1_SERVING: 1,
  PLAYER2_SERVING: 2,
} as const;

export type ServerTrackingType = typeof ServerTracking[keyof typeof ServerTracking];

/**
 * Helper function to check if a match is in a terminal status
 * (completed, retired, walkover, or default)
 */
export function isMatchTerminal(status: number): boolean {
  return (
    status === MatchStatus.MATCH_COMPLETED ||
    status === MatchStatus.MATCH_RETIRED ||
    status === MatchStatus.MATCH_WALKOVER ||
    status === MatchStatus.MATCH_DEFAULT
  );
}

/**
 * Helper function to check if umpire mode tracks serve
 * Per ScoreboardData.kt, only CU_BASIC, BEACH_TENNIS, and PICKLEBALL track serve
 */
export function tracksServe(umpireMode: number): boolean {
  return (
    umpireMode === UmpireMode.UMPIRE_MODE_CU_BASIC ||
    umpireMode === UmpireMode.UMPIRE_MODE_BEACH_TENNIS ||
    umpireMode === UmpireMode.UMPIRE_MODE_PICKLEBALL
  );
}

/**
 * Helper function to check if umpire mode shows game score
 * Per ScoreboardData.kt:91-93, only CU_BASIC, POINT_BY_POINT, and BEACH_TENNIS show game scores
 * Pickleball is deliberately excluded as it uses a different score display
 */
export function showsGameScore(umpireMode: number): boolean {
  return (
    umpireMode === UmpireMode.UMPIRE_MODE_CU_BASIC ||
    umpireMode === UmpireMode.UMPIRE_MODE_POINT_BY_POINT ||
    umpireMode === UmpireMode.UMPIRE_MODE_BEACH_TENNIS
  );
}

/**
 * Calculate pickleball server number (1 or 2) for doubles
 * Returns 0 for non-pickleball modes or singles
 *
 * Logic from ScoreboardData.kt pickleballServerNumber():
 * - Game starts 0-0-2 (starting team gets only one server before side-out per USA Pickleball 4.B.6)
 * - After game start, if current server is NOT the designated "server #1", show 2, otherwise show 1
 *
 * @param servingPlayer - Which player is currently serving (1 or 2)
 * @param isDouble - Whether this is a doubles match
 * @param umpireMode - The umpire mode
 * @param team1Score - Team 1's current score/points
 * @param team2Score - Team 2's current score/points
 * @param serverNumber1 - The player designated as server #1 (fixed at match start)
 * @param currentServingPlayer - The actual player currently serving
 * @returns 0 (not applicable), 1 (first server), or 2 (second server)
 */
export function calculatePickleballServerNumber(
  servingPlayer: number,
  isDouble: boolean,
  umpireMode: number,
  team1Score: number,
  team2Score: number,
  serverNumber1: number,
  currentServingPlayer: number
): number {
  if (umpireMode !== UmpireMode.UMPIRE_MODE_PICKLEBALL || !isDouble) {
    return 0;
  }

  const isGameStart = team1Score === 0 && team2Score === 0;

  // At game start or when the current server is NOT server #1, return 2
  // Otherwise return 1
  return isGameStart || currentServingPlayer !== serverNumber1 ? 2 : 1;
}

/**
 * Map numeric match status to display string
 * Useful for UI labels
 */
export function getMatchStatusLabel(status: number): string {
  switch (status) {
    case MatchStatus.MATCH_SCHEDULED:
      return "Scheduled";
    case MatchStatus.MATCH_NOT_STARTED:
      return "Not Started";
    case MatchStatus.MATCH_STARTED:
      return "In Progress";
    case MatchStatus.MATCH_COMPLETED:
      return "Completed";
    case MatchStatus.MATCH_SUSPENDED:
      return "Suspended";
    case MatchStatus.MATCH_RETIRED:
      return "Retired";
    case MatchStatus.MATCH_LOCKED:
      return "Locked";
    case MatchStatus.MATCH_WALKOVER:
      return "Walkover";
    case MatchStatus.MATCH_DEFAULT:
      return "Default";
    case MatchStatus.MATCH_INTERRUPTED:
      return "Interrupted";
    case MatchStatus.MATCH_MANUAL:
      return "Manual";
    default:
      return "Unknown";
  }
}
